import { useEffect } from 'react';
import { CheckCircle2, Leaf, Heart, Award, Waves, Sprout, Sparkles, Mountain, Fish } from 'lucide-react';
import { images, designPhases, environmentalBenefits, ecosystemComponents } from '../mock/data';

const LOGO_VIDEO_URL = process.env.REACT_APP_LOGO_VIDEO_URL;

export const About = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in-section');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const iconMap = {
    Leaf: Leaf,
    Heart: Heart,
    Award: Award,
    CheckCircle2: CheckCircle2,
    Waves: Waves,
    Sprout: Sprout,
    Sparkles: Sparkles,
    Mountain: Mountain,
    Fish: Fish
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <section className="pt-32 pb-24 px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-light text-stone-800 mb-8 tracking-tight animate-fade-in-up">
            About Pond Skape
          </h1>
          <p className="text-xl text-stone-600 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            A landscape water design studio creating site-responsive environments shaped by land, climate, and human experience.
          </p>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-24 px-6 lg:px-12 fade-in-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative h-[600px] rounded-sm overflow-hidden shadow-xl">
              <img
                src={images.about}
                alt="Tushar Chhetri - Founder"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl font-light text-stone-800 mb-6 tracking-tight">
                Founded by Tushar Chhetri
              </h2>
              <div className="space-y-6 text-stone-600 leading-relaxed">
                <p>
                  Pond Skape was founded on the belief that water should feel naturally connected to its landscape—not imposed upon it.
                </p>
                <p>
                  Tushar Chhetri saw that most water projects followed fixed templates—swimming environments, ornamental installations, predefined models. They lacked response to actual site conditions and lived experience.
                </p>
                <p>
                  Today, Pond Skape designs landscape-integrated water environments for residential, hospitality, and collaborative projects. Each is shaped by terrain, climate, and the feeling it is meant to create.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Studio Philosophy */}
      <section className="py-24 bg-stone-900 text-white fade-in-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-12 tracking-tight">
            Our Philosophy
          </h2>
          <div className="space-y-8 text-lg leading-relaxed">
            <p className="text-stone-300">
              Water shaped by land, climate, and human experience. From quiet reflection to flowing natural movement—every project responds to its surroundings.
            </p>
            <p className="text-stone-300">
              We design from context. Land before form. Flow before control. Experience before decoration. Ecology where appropriate.
            </p>
            <p className="text-stone-300">
              Nothing is standard. Every environment is born from place—designed for real conditions and actual use.
            </p>
          </div>
        </div>
      </section>

      {/* Design Values */}
      <section className="py-24 px-6 lg:px-12 fade-in-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-16 text-center tracking-tight">
            What Guides Our Work
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-stone-800 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="text-white" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-light text-stone-800 mb-4">Site-Responsive Design</h3>
                <p className="text-stone-600 leading-relaxed">
                  Every project begins with the land—slope, drainage, sunlight, climate. Form follows environment, not templates.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-stone-800 rounded-full flex items-center justify-center">
                  <Leaf className="text-white" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-light text-stone-800 mb-4">Experience-Driven</h3>
                <p className="text-stone-600 leading-relaxed">
                  Water changes how a space feels—through movement, reflection, sound, cooling, and seasonal change.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-stone-800 rounded-full flex items-center justify-center">
                  <Heart className="text-white" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-light text-stone-800 mb-4">Real Conditions</h3>
                <p className="text-stone-600 leading-relaxed">
                  We design for how systems are actually used—variable operation, changing climate, maintenance realities.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-stone-800 rounded-full flex items-center justify-center">
                  <Award className="text-white" size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-light text-stone-800 mb-4">Long-term Stability</h3>
                <p className="text-stone-600 leading-relaxed">
                  Durable containment, natural materials, and designs that remain structurally stable and visually resilient over time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Approach */}
      <section className="py-24 bg-white fade-in-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-16 text-center tracking-tight">
            Our Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {designPhases.map((phase, index) => (
              <div
                key={index}
                className="p-8 bg-stone-50 rounded-sm border border-stone-200 hover:border-stone-400 hover:shadow-lg transition-all duration-300"
              >
                <div className="text-6xl font-light text-stone-300 mb-4">{phase.number}</div>
                <h3 className="text-xl font-medium text-stone-800 mb-3">{phase.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem Components */}
      <section className="py-24 px-6 lg:px-12 bg-stone-50 fade-in-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-6 tracking-tight">
              The Complete Ecosystem
            </h2>
            <p className="text-lg text-stone-600 max-w-3xl mx-auto leading-relaxed">
              Every Pond Skape water feature includes these essential components working together to create a balanced, self-sustaining ecosystem.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ecosystemComponents.map((component, index) => {
              const IconComponent = iconMap[component.icon] || Leaf;
              return (
                <div
                  key={index}
                  className="p-8 bg-white rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-stone-800 rounded-full flex items-center justify-center mb-6">
                    <IconComponent className="text-white" size={28} />
                  </div>
                  <h3 className="text-lg font-medium text-stone-800 mb-3">{component.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{component.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Environmental Impact */}
      <section className="py-24 px-6 lg:px-12 bg-white fade-in-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-6 tracking-tight">
              Benefits Beyond Beauty
            </h2>
            <p className="text-lg text-stone-600 max-w-3xl mx-auto leading-relaxed">
              A well-designed water feature brings more than visual appeal—it creates positive environmental impact right in your backyard.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {environmentalBenefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center p-8 bg-stone-50 rounded-sm border border-stone-200 hover:border-stone-400 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Leaf className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-medium text-stone-800 mb-3">{benefit.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo Animation - Bottom of Page */}
      <section className="py-12 px-6 lg:px-12 bg-stone-50 fade-in-section">
        <div className="max-w-2xl mx-auto">
          <div className="relative rounded-sm overflow-hidden shadow-lg">
            <video
              className="w-full h-auto"
              autoPlay
              playsInline
            >
              <source src={LOGO_VIDEO_URL} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>
    </div>
  );
};
